import {
  Input, Label, ComboBox, Button,
} from '@components/common/Forms';
import { LocationFilterForm } from '@type/filters';
import { useTranslation } from 'next-i18next';
import { ApiLocationTooltipOk } from '@type/v1-api-types/common';
import { useLocationsBuilder } from '@hooks/form-helpers/useLocationsBuilder';
import {
  bjToast,
  Chip, PopoverStyled, PopoverStyledButton, PopoverStyledPanel,
  Tooltip,
} from '@components/common';
import { toSeparatorCase } from '@utils/strings/convertCase';
import {
  useMemo, useState, useEffect, useRef,
} from 'react';
import { mergeFilterValues } from '@components/features/filters/helpers/functions';
import { LocationCrosshairIcon } from '@root/src/components/common/Icons';
import { getAddressComponentsFromCoordinates } from '@root/src/utils/google-maps';
import { APIProvider } from '@vis.gl/react-google-maps';
import { cn } from '@root/src/utils/cn';

type LocationFormProps = LocationFilterForm & { isActive?: boolean };

/**
 * @description The location filter fields.
 * * **recordFunc** - the 'record' function from useBjForm.
 * * **submitFunc** - the function to trigger the form submit. You might want to trigger it on blur. Defaults to undefined.
 * * **verticalSpacing** - the spacing between the items. Defaults to 'gap-2'.
 * * **idPrefix** - the prefix for the form elements ids. When using multiple forms on the same page,
 * this is needed to avoid duplicate ids.
 */
export const LocationForm = (props: LocationFormProps) => {
  // Destructure props
  const {
    recordFunc,
    submitFunc,
    verticalSpacing = 'gap-2',
    idPrefix,
    withRadiusInKm = true,
    withSubmitButton = false,
    withSuggestedLocations = false,
    withUserLocation = false,
    closePopover,
    filterLabels,
    withPopover,
    keepAsString = false,
    isActive,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Use Locations Builder Hook
  const {
    comboboxOptions,
    selectedLocations,
    radiusArray,
    range,
    selectedLocationsString,
    selectedLocationsFiltersLabel,
    buildSelectedLocationsFromString,
    deleteLocation,
    setRange,
    addLocation,
  } = useLocationsBuilder();

  const [userLocality, setUserLocality] = useState<string | null>(null);

  // Suggested locations
  // ***********************************************
  const suggestedLocations = useMemo(() => [
    {
      label: t('popular.search.filters.abroad'),
      slug: toSeparatorCase(t('popular.search.filters.abroad')),
    },
    {
      label: t('popular.search.filters.bucuresti'),
      slug: toSeparatorCase(t('popular.search.filters.bucuresti')),
    },
    {
      label: t('popular.searches.filters.iasi'),
      slug: toSeparatorCase(t('popular.searches.filters.iasi')),
    },
    {
      label: t('popular.searches.filters.cluj'),
      slug: toSeparatorCase(t('popular.searches.filters.cluj')),
    },
  ], [t]);

  // Local ref in case ComboBox forwards it in the future; we’ll also fall back to querySelector
  const locationInputRef = useRef<HTMLInputElement | null>(null);

  // When the form becomes active, focus its input (localized, React-friendly)
  useEffect(() => {
    if (!isActive) return;
    // Prefer the ref if your ComboBox forwards it (kept here for future compatibility)
    if (locationInputRef.current) {
      locationInputRef.current.focus();
      try { locationInputRef.current.select?.(); } catch { /* ignore */ }
      return;
    }
    // Fallback: target the existing test id that wraps the combobox input
    const el = document.querySelector<HTMLInputElement>('[data-test-id="filters-location-input"]');
    if (el) {
      el.focus();
      try { el.select?.(); } catch { /* ignore */ }
    }
  }, [isActive]);

  // Suggested Locations Filtered
  // ***********************************************
  const suggestedLocationsFiltered = useMemo(
    () => suggestedLocations.filter(
      (location) => !selectedLocations.some((loc) => loc === location.label || loc === location.slug),
    ),
    [selectedLocations, suggestedLocations],
  );

  const renderHiddenInput = () => (
    <Input
      type="hidden"
      value={selectedLocationsString}
      data-filters-label={selectedLocationsFiltersLabel}
      onInput={(e) => {
        if (!e.isTrusted) buildSelectedLocationsFromString(e);
      }}
      {...recordFunc('location', {
        id: `${idPrefix || ''}location`,
        convertValue: keepAsString ? 'toString' : 'commaStringToArray',
        ...(withRadiusInKm && {
          removeFieldsIfThisEmpty: ['radiusInKm'],
        }),
      })}
    />
  );

  const popoverButtonLabel = useMemo(() => {
    const locations = filterLabels ? mergeFilterValues([filterLabels.location], t) : selectedLocationsFiltersLabel;

    return `${t('global.location.label')}: ${locations || t('global.action.choose')}`;
  }, [filterLabels, selectedLocationsFiltersLabel, t]);


  const handleUseMyLocationClick = () => {
    // If we already have user locality use that instead of refetching
    if (userLocality) {
      addLocation(userLocality);
      return;
    }

    const handlePosition = async (position: GeolocationPosition) => {
      const { latitude, longitude } = position.coords;

      const addressComponents = await getAddressComponentsFromCoordinates({
        lat: latitude,
        lng: longitude,
      });

      if (!addressComponents) {
        bjToast.error(t('global.something.went.wrong'));
        return;
      }

      const locality = addressComponents.find((component) => component.types.includes('locality'))?.long_name;

      if (!locality) {
        bjToast.error(t('global.something.went.wrong'));
        return;
      }

      addLocation(locality);
      setUserLocality(locality);
      submitFunc?.();
    };

    navigator.geolocation.getCurrentPosition((position) => {
      void handlePosition(position);
    }, (error) => {
      if (error.code === 1) {
        // Permission denied
        bjToast.error(t('global.error.current_location.permission_denied'));
        return;
      }

      bjToast.error(t('global.something.went.wrong'));
    });
  };


  const googleApiKey = process.env.GOOGLE_MAPS_CLIENT_API_KEY as string;

  // Render component
  // **********************************************
  const LocationFilterContent = (
    <div className={`grid ${verticalSpacing}`}>
      {/* Location combobox */}
      <Label
        description={t('filters.location.combobox.label')}
        descriptionHelper={`(${t('global.combobox.box-type')}, ${t('form.validity.min-chars-3')})`}
        descriptionClass="font-bold !mb-2 text-ink-medium"
        className="!mt-0"
        as="div"
      >
        <div className="relative flex items-center">
          {withUserLocation && (
            <APIProvider
              apiKey={googleApiKey}
            >
              <Tooltip tooltipText={t('global.label.current_location')} floating className="absolute left-2 z-1" position="top" triggerContainerClass="flex" colorScheme="black">
                <Button styling="none" className="box-content size-5" onClick={handleUseMyLocationClick}>
                  <LocationCrosshairIcon className={cn('size-full', { 'text-primary': userLocality && selectedLocations.some((loc) => loc === userLocality || loc === toSeparatorCase(userLocality)) })} />
                </Button>
              </Tooltip>
            </APIProvider>
          )}
          <ComboBox
            data-test-id="filters-location-input"
            minLength={3}
            placeholder={t('cv-value-label-city-placeholder')}
            options={comboboxOptions}
            renderList={(item, index, listKey, getItemProps, listStyling, highlightedIndex) => {
              // add a type to make TS behave nicely; we already checked for errors object inside Combobox fetch
              const location = item as ApiLocationTooltipOk;
              return (
                <li key={listKey} className={`${listStyling} flex-col`} {...getItemProps({ item, index })}>
                  <span>{location.locality}</span>
                  <span className={`${highlightedIndex === index ? 'text-white' : 'text-ink-medium'} -mt-0.5 text-tiny font-normal transition-all duration-150`}>
                    {location.country}
                  </span>
                </li>
              );
            }}
            onSelectedItemChange={(item) => {
              // Add the selected location to show as pill
              const location = item as ApiLocationTooltipOk;
              if (location) {
                addLocation(location.locality);
                submitFunc?.();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            className={cn('valid:!border-input', { 'pl-9': withUserLocation })}
            {...recordFunc('locations', {
              id: `${idPrefix || ''}locations`,
              removeFromSubmit: true,
            })}
          />
        </div>
      </Label>

      {/* Selected locations */}
      {!!selectedLocations?.length && (
        <div className="mt-1 flex flex-wrap gap-2">
          {selectedLocations.map((location) => (
            <Chip key={location} onDelete={() => deleteLocation(location)}>
              {location}
            </Chip>
          ))}
        </div>
      )}

      {/* Suggested / popular cities */}
      {withSuggestedLocations && !!suggestedLocationsFiltered.length && (
        <div className="mt-4">
          <span className="mb-1.5 block text-sm font-bold text-ink-medium">
            {t('filters.location.suggested_locations')}
          </span>
          <div className="flex flex-wrap gap-2">
            {suggestedLocationsFiltered.map((location) => (
              <Chip
                key={location.slug}
                highlighted={selectedLocations.includes('location.label')}
                onClick={() => {
                  addLocation(location.label);
                  submitFunc?.();
                }}
              >
                {location.label}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {!withPopover && renderHiddenInput() }

      {/* Radius filter */}
      {withRadiusInKm && (
        <Label
          htmlFor={`${idPrefix || ''}radiusInKm`}
          description={t('filters.location.radius.label')}
          descriptionClass="font-bold !mb-0 text-ink-medium"
        >
          <Input
            type="range"
            min={0}
            max={100}
            step={10}
            className="h-1.5 w-full accent-secondary"
            list="range-labels"
            data-filters-label={`+${range} km`}
            onInput={(e) => setRange(Number(e.currentTarget.value))}
            disabled={!selectedLocations.length}
            {...(submitFunc ? { onBlur: submitFunc } : {})}
            {...recordFunc('radiusInKm', {
              id: `${idPrefix || ''}radiusInKm`,
              defaultValue: 30,
              dontHardReset: true,
            })}
          />

          <datalist id="range-labels" className="-mx-1 flex justify-between border-secondary text-center text-pico text-ink-medium">
            {// generate the options for the radius filter
              radiusArray.map((item) => (
                <option
                  key={item}
                  className="w-6 pt-1 text-center"
                  value={item}
                  label={item.toString()}
                />
              ))
            }
          </datalist>
        </Label>
      )}

      {/* Submit button */}
      {withSubmitButton && !withPopover && (
        <Button
          type="button"
          color="secondary"
          rounding="full"
          size="sm"
          className="mx-auto mt-4"
          onClick={() => {
            submitFunc?.();
            closePopover?.();
          }}
        >
          {t('filters.location.show_result')}
        </Button>
      )}
    </div>
  );

  return (
    <>
      {withPopover ? (
        <>
          {withRadiusInKm && (
            <Input
              type="hidden"
              value={range}
              data-filters-label={`+${range} km`}
              {...recordFunc('radiusInKm', {
                id: `${idPrefix || ''}radiusInKm`,
                defaultValue: range,
                dontHardReset: true,
              })}
            />
          )}
          {renderHiddenInput()}
          <PopoverStyled>
            {({ open, close }) => (
              <>
                {/* Chip Button */}
                <PopoverStyledButton
                  as={Chip}
                  highlighted={open}
                  isDropdownOpen={open}
                  withDropdownArrow
                >
                  {popoverButtonLabel}
                </PopoverStyledButton>

                {/* Popover Panel */}
                <PopoverStyledPanel
                  animation="slideDown"
                  anchor="bottom"
                  unmount={false}
                  className="!z-9 mt-1 !max-h-none w-80 !rounded-md p-4"
                >
                  <div className="mb-2 text-sm text-ink-light">{t('filters.location.popover.title')}</div>
                  {LocationFilterContent}

                  <div className="text-center">
                    {withSubmitButton && (
                      <Button
                        type="button"
                        color="secondary"
                        rounding="full"
                        size="sm"
                        className="mx-auto mt-4"
                        onClick={() => {
                          submitFunc?.();
                          close();
                        }}
                      >
                        {t('filters.location.show_result')}
                      </Button>
                    )}
                  </div>
                </PopoverStyledPanel>
              </>
            )}
          </PopoverStyled>
        </>
      ) : (LocationFilterContent)}
    </>
  );
};

